import { Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Condominium, CondominiumService, CreateCondominiumRequest } from '../../services/condominium.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

type CondominiumFormMode = 'create' | 'edit';

// TODO: especificar modo de edição melhor

@Component({
    selector: 'app-condominium-form',
    templateUrl: './condominium-form.component.html',
    styleUrls: ['./condominium-form.component.sass'],
    standalone: false
})
export class CondominiumFormComponent {
  @Input() mode: CondominiumFormMode = 'create';
  @Input() condominiumId?: number;
  @Input() showTermsAcceptance = false;

  form!: UntypedFormGroup;
  isSaving = false;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly uppercaseInput = (value: unknown): string => String(value ?? '').toUpperCase();

  constructor(
    private fb: UntypedFormBuilder,
    private condominiumService: CondominiumService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  get phoneNumbers(): UntypedFormArray {
    return this.form.get('phoneNumbers') as UntypedFormArray;
  }

  async ngOnInit() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      email: [''],
      cnpj: [''],
      address: this.fb.group({
        street: ['', Validators.required],
        number: ['', Validators.required],
        complement: [''],
        neighborhood: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        country: ['', Validators.required],
        zipCode: ['']
      }),
      phoneNumbers: this.fb.array([])
    });

    if (this.showTermsAcceptance) {
      this.form.addControl('acceptedTermsOfUse', this.fb.control(false, [Validators.requiredTrue]));
      this.form.addControl('acceptedPrivacyPolicy', this.fb.control(false, [Validators.requiredTrue]));
    }

    if (this.mode === 'edit') {
      if (!this.condominiumId && this.condominiumId !== 0) {
        this.snackBar.open('ID do condomínio ausente para edição', 'OK', { duration: 3000 });
        return;
      }
      this.loadFromApi();
    }
  }

  private fillFormFromCondominium(condominium: Condominium): void {
    this.form.patchValue({
      name: condominium.name,
      email: condominium.email || '',
      cnpj: condominium.cnpj || '',
      address: {
        street: condominium.address.street,
        number: condominium.address.number,
        complement: condominium.address.complement || '',
        neighborhood: condominium.address.neighborhood || '',
        city: condominium.address.city,
        state: condominium.address.state,
        country: condominium.address.country,
        zipCode: condominium.address.zipCode || ''
      }
    });
    while (this.phoneNumbers.length) {
      this.phoneNumbers.removeAt(0);
    }
    (condominium.phoneNumbers || []).forEach(p => this.phoneNumbers.push(this.fb.control(p)));
    const cnpjValue = (condominium.cnpj || '').trim();
    if (cnpjValue) {
      this.form.get('cnpj')?.disable();
    } else {
      this.form.get('cnpj')?.enable();
    }
  }

  private loadFromApi(): void {
    this.condominiumService.getCondominiumDetails(this.condominiumId!).subscribe({
      next: (c) => {
        this.fillFormFromCondominium(c);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      },
      error: () => {
        this.snackBar.open('Falha ao carregar condomínio', 'OK', { duration: 3000 });
      }
    });
  }

  public reload(): void {
    if (this.mode !== 'edit') { return; }
    if (!this.condominiumId && this.condominiumId !== 0) {
      this.snackBar.open('ID do condomínio ausente para recarregar', 'OK', { duration: 3000 });
      return;
    }
    this.loadFromApi();
  }

  addPhone(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.phoneNumbers.push(this.fb.control(value));
    }
    if (event.input) {
      event.input.value = '';
    }
  }

  removePhone(index: number): void {
    this.phoneNumbers.removeAt(index);
  }

  // Returns false (and shows a snackbar) when terms acceptance is required but missing.
  public validateTermsAcceptance(): boolean {
    if (!this.showTermsAcceptance) {
      return true;
    }
    const acceptedTerms = this.form.get('acceptedTermsOfUse')?.value === true;
    const acceptedPrivacy = this.form.get('acceptedPrivacyPolicy')?.value === true;
    if (!acceptedTerms && !acceptedPrivacy) {
      this.snackBar.open('Você precisa aceitar os termos de uso e a política de privacidade para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    if (!acceptedTerms) {
      this.snackBar.open('Você precisa aceitar os termos de uso para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    if (!acceptedPrivacy) {
      this.snackBar.open('Você precisa aceitar a política de privacidade para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    return true;
  }

  public save(): void {
    if (!this.validateTermsAcceptance()) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    const request: CreateCondominiumRequest = {
      ...this.form.value,
      phoneNumbers: this.phoneNumbers.value
    };

    const nextHandler = () => {
      this.snackBar.open(
        this.mode === 'create' ? 'Condomínio criado com sucesso' : 'Condomínio atualizado com sucesso',
        'OK',
        { duration: 3000 }
      );
      this.router.navigate(['/condominiums']);
    };
    const errorHandler = () => {
      this.isSaving = false;
      this.snackBar.open(
        this.mode === 'create' ? 'Erro ao criar condomínio' : 'Erro ao atualizar condomínio',
        'OK',
        { duration: 3000 }
      );
    };

    if (this.mode === 'create') {
      this.condominiumService.createCondominium(request).subscribe({
        next: nextHandler,
        error: errorHandler,
        complete: () => { this.isSaving = false; }
      });
    } else {
      if (!this.condominiumId && this.condominiumId !== 0) {
        this.isSaving = false;
        this.snackBar.open('ID do condomínio ausente para salvar', 'OK', { duration: 3000 });
        return;
      }
      this.condominiumService.updateCondominium(this.condominiumId!, request).subscribe({
        next: nextHandler,
        error: errorHandler,
        complete: () => { this.isSaving = false; }
      });
    }
  }
}


